import { Field } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { useDocumentAnnotations, usePredictionAnnotations } from '@/hooks';
import { toModelId, toPredictionId, useGetModel, useGetPrediction } from '@/hooks/api';
import { Annotation } from '@/utils';

export type ForEachHandlerArgs = {
  fieldId: string;
  config: Field;
  predictionAnnotation?: Annotation | null;
  documentAnnotation?: Annotation | null;
  parentFieldId?: string | null;
  parentConfig?: Field | null;
};
export type ForEachHandler = (args: ForEachHandlerArgs) => void;

export type UseAnnotationsOpts = {
  predictionId?: string | null;
  documentId?: string | null;
};

export const useAnnotations = ({ predictionId, documentId }: UseAnnotationsOpts) => {
  const { annotations: predictionAnnotations } = usePredictionAnnotations({ predictionId });
  const { annotations: documentAnnotations } = useDocumentAnnotations({ documentId });
  const { data: prediction } = useGetPrediction(toPredictionId(predictionId));
  const { data: model } = useGetModel(toModelId(prediction?.modelId));

  const forEach = useCallback(
    (handler: ForEachHandler) => {
      for (const [fieldId, config] of Object.entries(model?.fieldConfig ?? {})) {
        if (config.type === 'single-value') {
          /* Only look at top prediction and top ground truth for single-value fields */
          const predictionAnnotation = predictionAnnotations?.[fieldId]?.[0];
          const documentAnnotation = documentAnnotations?.[fieldId]?.[0];

          handler({ fieldId, config, predictionAnnotation, documentAnnotation });
        } else if (config.type === 'multi-value') {
          /* Look at all predictions and corresponding ground truths for multi-value fields */
          const predictionMultiValueAnnotations = predictionAnnotations?.[fieldId] ?? [];
          const documentMultiValueAnnotations = documentAnnotations?.[fieldId] ?? [];
          const maxLength = Math.max(predictionMultiValueAnnotations.length, documentMultiValueAnnotations.length);

          for (let i = 0; i < maxLength; ++i) {
            const predictionAnnotation = predictionMultiValueAnnotations[i];
            const documentAnnotation = documentMultiValueAnnotations[i];

            handler({ fieldId, config, predictionAnnotation, documentAnnotation });
          }
        } else if (config.type === 'table') {
          /* Look at all row predictions and corresponding ground truth for table fields */
          const predictionTableAnnotations = predictionAnnotations?.[fieldId] ?? [];
          const documentTableAnnotations = documentAnnotations?.[fieldId] ?? [];
          const maxLength = Math.max(predictionTableAnnotations.length, documentTableAnnotations.length);

          for (let i = 0; i < maxLength; ++i) {
            const predictionRowAnnotations = predictionTableAnnotations[i] ?? {};
            const documentRowAnnotations = documentTableAnnotations[i] ?? {};

            for (const [subFieldId, subConfig] of Object.entries(config.fields ?? {})) {
              /* TODO: Handle multi-value and table within table */
              if (subConfig.type === 'single-value') {
                /* Only look at top prediction and top ground truth for table cells */
                const predictionAnnotation = predictionRowAnnotations[subFieldId]?.[0];
                const documentAnnotation = documentRowAnnotations[subFieldId]?.[0];

                handler({
                  fieldId: subFieldId,
                  config: subConfig,
                  predictionAnnotation,
                  documentAnnotation,
                  parentFieldId: fieldId,
                  parentConfig: config,
                });
              }
            }
          }
        }
      }
    },
    [documentAnnotations, model, predictionAnnotations]
  );

  return { forEach };
};
